import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import Button from 'components/StyledButton/StyledButton';
import ExternalHeader from 'pages/plasticAssessment/components/Header/Header';
import Company from 'types/Company';

import StyledActionButton from '../../../../common/styled/StyledActionButton/StyledActionButton';
import CustomLoader from '../../../../components/CustomLoader/CustomLoader';
import DeviationDataDetailsDialog from '../../../../dialogs/DeviationDataDetailsDialog/DeviationDataDetailsDialog';
import Deviation from '../../../../types/Deviation';
import DeviationData from '../../../../types/DeviationData';
import api from '../../../../utils/api';
import { downloadFile } from '../../../../utils/download';
import { ActionTableCell, FlexContainer } from '../../styled';

interface Props {
    companyData: Company;
    companyId: number;
    assessmentYear: number | null;
    tabsRender: JSX.Element;
    isCustomer: boolean;
    fullWidthSelector: boolean;
    exportWholePdf: () => void;
    setIsCompanyDialogOpen: Dispatch<SetStateAction<boolean>>;
}

const DeviationsTab = (props: Props) => {
    const { companyData, companyId, assessmentYear, tabsRender, isCustomer, fullWidthSelector, exportWholePdf, setIsCompanyDialogOpen } = props;

    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(true);
    const [deviations, setDeviations] = useState<DeviationData[]>([]);
    const [selectedItemId, selectItemId] = useState(0);

    const [isItemDetailsDialogOpen, setIsItemDetailsDialogOpen] = useState(false);

    const getData = useCallback(() => {
        api.request('/companies/' + companyId + '/deviations').then((res) => {
            setDeviations(res);
            setIsLoading(false);
        });
    }, [companyId]);

    const exportData = useCallback((format: 'csv' | 'pdf') => {
        api.request('/companies/' + companyId + '/deviations/export/' + format, 'GET', null, true, 'blob').then(res => {
            downloadFile(res, 'export.' + format);
        });
    }, [companyId]);

    const openItemDialog = useCallback((itemId: number) => {
        selectItemId(itemId);
        setIsItemDetailsDialogOpen(true);
    }, []);

    const closeItemDialog = useCallback((itemId?: number) => {
        if (itemId) {
            selectItemId(itemId);
        }
        setIsItemDetailsDialogOpen(false);
    }, []);

    const getDeviationDescription = useCallback((deviation: Deviation) => {
        const languageCode = t('language_code');

        if (languageCode === 'it-IT') {
            return deviation.description;
        }

        const language = deviation.languages.find(lang => lang.code === languageCode);

        if (language && language.translation) {
            return language.translation;
        }

        return deviation.description;
    }, [t]);

    useEffect(() => {
        if (!isItemDetailsDialogOpen) getData();
    }, [getData, isItemDetailsDialogOpen]);


    if (isLoading) {
        return (
            <>
                <ExternalHeader companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
                <FlexContainer>
                    {tabsRender}
                </FlexContainer>
                <CustomLoader />
            </>
        );
    }

    return (
        <>
            <ExternalHeader companyName={companyData.name} assessmentYear={assessmentYear} setIsCompanyDialogOpen={setIsCompanyDialogOpen} />
            {fullWidthSelector && (
                <div style={{ marginBottom: '14px' }}>
                    {tabsRender}
                </div>
            )}
            <FlexContainer>
                {!fullWidthSelector && <>{tabsRender}</>}
                <div>
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('csv')} >
                        {t('pages.plastic_assessment.export_csv')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportData('pdf')} >
                        {t('pages.plastic_assessment.export_pdf')}
                    </StyledActionButton>{' '}
                    <StyledActionButton color='primary' variant='outlined' onClick={() => exportWholePdf()} >
                        {t('pages.plastic_assessment.export_whole_pdf')}
                    </StyledActionButton>
                </div>
            </FlexContainer>

            <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='info'>
                <Trans i18nKey='pages.plastic_assessment.tabs.deviations.help'>
                    _<AlertTitle>_</AlertTitle>_
                    <b>_</b>
                </Trans>
            </Alert>

            <TableContainer component={Paper} style={{ marginTop: '20px' }} className='book-list-table-view'>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.deviations.table.phase')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.deviations.table.deviation')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.deviations.table.present')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.deviations.table.quantity')}</TableCell>
                            <TableCell>{t('pages.plastic_assessment.tabs.deviations.table.resolution_date')}</TableCell>
                            <ActionTableCell>{t('pages.companies.table.actions')}</ActionTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {deviations.map((data, index) => {
                            return (
                                <TableRow key={data.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{t('models.deviation.enum.category.' + data.deviation.category)}</TableCell>
                                    <TableCell>{getDeviationDescription(data.deviation)}</TableCell>
                                    <TableCell>{t('pages.generic.table.flag_' + (data.deviationPresent ? 'yes' : 'no'))}</TableCell>
                                    <TableCell>{data.deviationCount}</TableCell>
                                    <TableCell>{data.deviationResolutionDate}</TableCell>
                                    <ActionTableCell>
                                        <Button variant='outlined' color='primary' onClick={() => openItemDialog(data.id)}>
                                            {t('pages.generic.table.details')}
                                        </Button>
                                    </ActionTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <DeviationDataDetailsDialog open={isItemDetailsDialogOpen} onClose={closeItemDialog} companyId={companyId} dataId={selectedItemId} getDeviationDescription={getDeviationDescription} isCustomer={isCustomer} />
        </>
    );
};

export default DeviationsTab;
